import React from 'react'
import { useRouteMatch, useHistory, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import clsx from 'clsx'
import Container from '@material-ui/core/Container'
import Avatar from '@material-ui/core/Avatar'
import Hidden from '@material-ui/core/Hidden'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import PersonIcon from 'components/material-symbol/icons/Person'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import AccessTimeOutlinedIcon from 'components/material-symbol/icons/AccessTime'
import CheckCircleIcon from 'components/material-symbol/icons/CheckCircle'
import RemoveCircleIcon from 'components/material-symbol/icons/RemoveCircle'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import SettingsOutlinedIcon from 'components/material-symbol/icons/Settings'
import DeleteOutlineOutlined from 'components/material-symbol/icons/DeleteOutline'
import PersonOutlineOutlinedIcon from 'components/material-symbol/icons/PersonOutline'
import StarBorderOutlinedIcon from 'components/material-symbol/icons/StarBorder'
import VisibilityOutlinedIcon from 'components/material-symbol/icons/Visibility'
import LockIcon from 'components/material-symbol/icons/Lock'
import { GET_FACILITY } from 'components/facilities/api-hooks'
import { PageHeader } from 'components/page-header'
import RouterLinkButton from 'components/router-link-button'
import Error from 'components/error'
import { FormattedOneLineAddress } from 'components/util/formatted_address'
import Disclaimer from 'components/disclaimer'
import ButtonCta from 'components/atoms/button-cta'
import { useAuth } from 'lib/auth'
import { useIsMobile } from 'lib/hooks'
import {
  DetailField,
  DetailSection,
} from 'pages/pga-coach/students/student-show-page/details-components/content-box'
import DeleteFacilityDialog from './delete-facility-dialog'
import { useStyles } from './styles'

const DAYS_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]

const formatDayLabel = day => `${day.charAt(0)}${day.slice(1).toLowerCase()}`

const formatAddress = ({ address1, address2, city, state, zip, url }) => {
  if (address1) {
    return (
      <FormattedOneLineAddress
        address1={address1}
        address2={address2}
        city={city}
        state={state}
        zip={zip}
      />
    )
  }

  return url || 'N/A'
}

const formatHeaderSubtitle = ({ city, state }) =>
  [city, state].filter(Boolean).join(', ')

const AvailabilityList = ({ availability }) => {
  const aggregateByDay = timeSlots => {
    const initialValue = DAYS_OF_WEEK.reduce((acc, d) => {
      acc[d] = []
      return acc
    }, {})
    return (timeSlots || []).reduce((acc, ts) => {
      acc[ts.day].push(ts)
      return acc
    }, initialValue)
  }

  const availabilityByDay = aggregateByDay(availability?.timeSlots)

  const renderTimeslots = timeSlots => {
    if (isEmpty(timeSlots)) return 'Not available'

    return timeSlots
      .map(ts => {
        const startTime = DateTime.fromISO(ts.startTime).toFormat('h:mma')
        const endTime = DateTime.fromISO(ts.endTime).toFormat('h:mma')
        return `${startTime} - ${endTime}`
      })
      .join(', ')
  }

  return DAYS_OF_WEEK.map(day => {
    const timeSlots = availabilityByDay[day]
    const isAvailable = !isEmpty(timeSlots)
    const Icon = isAvailable ? CheckCircleIcon : RemoveCircleIcon

    return (
      <DetailField
        key={day}
        icon={Icon}
        iconTone={isAvailable ? 'default' : 'unavailable'}
        label={formatDayLabel(day)}
        value={renderTimeslots(timeSlots)}
        testId={`facility-availability-${day.toLowerCase()}`}
      />
    )
  })
}

export const Facility = ({ facility }) => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const classes = useStyles()
  const isMobile = useIsMobile()
  const { user } = useAuth()
  const hasCoachProfile = user?.coach?.coachProfile

  const {
    id,
    name,
    address1,
    address2,
    city,
    state,
    zip,
    timezone,
    url,
    availabilities,
    private: isPrivate,
    coachProfilesFacilities,
  } = facility
  const isVisible = coachProfilesFacilities[0]?.visible
  const isPrimary = coachProfilesFacilities[0]?.facilityRank === 1
  const hasAvailabilities = !isEmpty(availabilities)
  const headerSubtitle = formatHeaderSubtitle({ city, state })

  const facilityPath = path
    .split('/')
    .slice(0, 4)
    .join('/')

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
    {
      label: 'Facilities',
      to: facilityPath,
    },
  ]

  return (
    <>
      <PageHeader
        title={name}
        crumbs={crumbs}
        hideCrumbs
        backTo={facilityPath}
        avatar={
          <Hidden xsDown>
            <Avatar className={classes.headerAvatar}>
              <LocationOnOutlinedIcon />
            </Avatar>
          </Hidden>
        }
        subtitle={headerSubtitle || undefined}
        actions={
          <RouterLinkButton
            variant="outlined"
            color="primary"
            startIcon={!isMobile && <EditOutlinedIcon />}
            to={{
              pathname: `${facilityPath}/${id}/edit`,
            }}
          >
            Edit
          </RouterLinkButton>
        }
        fixedMobileActions
      />
      {!hasCoachProfile && (
        <Container maxWidth="md">
          <Box mt={4}>
            <Disclaimer
              icon={<PersonOutlineOutlinedIcon />}
              description={
                <>
                  <Typography variant="subtitle1">
                    Create Your Coach Profile
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Once you create a profile, you will be able to manage how
                    this facility is displayed. You will also be able to set it
                    as your primary facility to be used for location-based
                    searches.
                  </Typography>
                </>
              }
              action={
                <ButtonCta
                  variant={isMobile ? 'text' : 'outlined'}
                  arrow={isMobile}
                  color="primary"
                  href={'/pga-coach/profile/new'}
                >
                  Create Profile
                </ButtonCta>
              }
            />
          </Box>
        </Container>
      )}
      <Container maxWidth={false} disableGutters className={classes.content}>
        <Box
          className={clsx(
            classes.infoCardsGrid,
            !hasCoachProfile && classes.infoCardsGridSingle,
          )}
        >
          {hasCoachProfile && (
            <DetailSection title="Coach profile visibility" compact stacked>
              <DetailField
                icon={VisibilityOutlinedIcon}
                label="Visibility"
                value={`${isVisible ? 'Visible' : 'Hidden'} on Profile`}
                testId="facility-visibility"
              />
              <DetailField
                icon={StarBorderOutlinedIcon}
                label="Primary facility"
                value={isPrimary ? 'Yes' : 'No'}
                testId="facility-primary"
              />
            </DetailSection>
          )}
          <DetailSection title="Facility details" compact stacked>
            <DetailField
              icon={PersonIcon}
              label="Coach profile"
              value={hasAvailabilities ? 'Active' : 'Inactive'}
              testId="facility-coach-profile"
            />
            <DetailField
              icon={LocationOnOutlinedIcon}
              label="Address"
              value={formatAddress({
                address1,
                address2,
                city,
                state,
                zip,
                url,
              })}
              testId="facility-address"
            />
            <DetailField
              icon={AccessTimeOutlinedIcon}
              label="Time zone"
              value={timezone || 'N/A'}
              testId="facility-timezone"
            />
            {isPrivate && (
              <DetailField
                icon={LockIcon}
                label="Privacy"
                value="Private facility"
                testId="facility-privacy"
              />
            )}
          </DetailSection>
        </Box>
        <DetailSection
          title="Coaching availability"
          stacked
          headerAction={
            <RouterLinkButton
              variant="outlined"
              color="primary"
              className={classes.headerActionButton}
              startIcon={!isMobile && <SettingsOutlinedIcon />}
              to="/pga-coach/settings/availability"
              data-testid="facility-manage-availability"
            >
              {isMobile ? 'Manage' : 'Manage Availability'}
            </RouterLinkButton>
          }
        >
          <AvailabilityList availability={availabilities?.[0]} />
        </DetailSection>
        <DetailSection title="Delete facility" layout="plain" compact>
          <Box className={classes.deleteFacility}>
            <Typography className={classes.deleteText}>
              Delete facility and all booking types and availability associated
              to this facility. When deleted, this facility will also be removed
              from your coach profile.
            </Typography>
            <DeleteFacilityDialog
              facilityId={id}
              onSuccess={() => history.push('/pga-coach/facilities')}
              trigger={
                <Button
                  startIcon={<DeleteOutlineOutlined />}
                  variant="outlined"
                  color="primary"
                  className={classes.deleteActionButton}
                  disabled={isPrimary}
                  data-testid="facility-delete-button"
                >
                  Delete
                </Button>
              }
            />
          </Box>
        </DetailSection>
      </Container>
    </>
  )
}

const FacilitiesPageShow = () => {
  const { id: facilityId } = useParams()

  const { loading, data, error } = useQuery(GET_FACILITY, {
    variables: { facilityId },
  })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error />
  const { facility } = data

  return <Facility facility={facility} />
}

export default FacilitiesPageShow
